import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { BODY_PORTAL_Z_INDEX } from './styles';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

// ============================================================================
// Types
// ============================================================================

export interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDangerous?: boolean;
  isInfo?: boolean;
}

// ============================================================================
// Animations
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Body-portaled: must clear the fullscreen chat (see BODY_PORTAL_Z_INDEX). */
  z-index: ${BODY_PORTAL_Z_INDEX};
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalCard = styled.div`
  background: var(--brand-canvas);
  border-radius: 18px;
  padding: 24px;
  width: 90%;
  max-width: 420px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 24px 64px rgba(15, 23, 42, 0.16),
    0 10px 28px rgba(15, 23, 42, 0.10);
  animation: ${scaleIn} 0.25s ease-out;
  text-align: left;
`;

const IconContainer = styled.div<{ $isDangerous?: boolean }>`
  width: 48px;
  height: 48px;
  margin: 0 0 16px;
  background: ${props => props.$isDangerous ? 'rgba(239, 68, 68, 0.10)' : 'rgba(33, 150, 243, 0.10)'};
  border: 1px solid ${props => props.$isDangerous ? 'rgba(239, 68, 68, 0.18)' : 'rgba(33, 150, 243, 0.18)'};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.$isDangerous ? '#ef4444' : '#2196f3'};
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 1.12rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const ModalMessage = styled.p`
  margin: 0 0 22px;
  font-size: 0.92rem;
  color: #4b5563;
  line-height: 1.55;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 10px 16px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 650;
  cursor: pointer;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.20);
  }
`;

const CancelButton = styled(ModalButton)`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #475569;

  &:hover {
    background: #f8fafc;
    border-color: rgba(0, 0, 0, 0.12);
  }
`;

const ConfirmButton = styled(ModalButton)<{ $isDangerous?: boolean }>`
  background: ${props => props.$isDangerous ? '#ef4444' : '#2196f3'};
  border: 1px solid ${props => props.$isDangerous ? '#dc2626' : '#1d8bd8'};
  color: white;
  box-shadow: ${props => props.$isDangerous ? '0 8px 18px rgba(239, 68, 68, 0.22)' : '0 8px 18px rgba(33, 150, 243, 0.22)'};

  &:hover {
    background: ${props => props.$isDangerous ? '#dc2626' : '#1d8bd8'};
    transform: translateY(-1px);
  }
`;

// ============================================================================
// Warning Icon
// ============================================================================

const WarningIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDangerous = false,
  isInfo: _isInfo = false,
}) => {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Keyboard handling and focus management
  useEffect(() => {
    if (!isOpen) return;

    // Focus the cancel button on open (safer default)
    cancelButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }

      // Focus trap: Tab cycles between Cancel and Confirm
      if (e.key === 'Tab') {
        const focusableElements = [cancelButtonRef.current, confirmButtonRef.current];
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onCancel]);

  // Prevent body scroll when modal is open — shared reference-counted lock
  // (2026-08-12 scrolling round) so overlapping overlays (fullscreen chat,
  // previews) can't restore each other's snapshots out of order.
  useBodyScrollLock(isOpen);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  }, [onCancel]);

  if (!isOpen) return null;

  const modalContent = (
    <ModalBackdrop
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-message"
      data-prest0n-portal="true"
    >
      <ModalCard>
        <IconContainer $isDangerous={isDangerous}>
          <WarningIcon />
        </IconContainer>

        <ModalTitle id="modal-title">{title}</ModalTitle>
        <ModalMessage id="modal-message">{message}</ModalMessage>

        <ButtonContainer>
          <CancelButton
            ref={cancelButtonRef}
            onClick={onCancel}
            type="button"
          >
            {cancelText}
          </CancelButton>
          <ConfirmButton
            ref={confirmButtonRef}
            onClick={onConfirm}
            $isDangerous={isDangerous}
            type="button"
          >
            {confirmText}
          </ConfirmButton>
        </ButtonContainer>
      </ModalCard>
    </ModalBackdrop>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
